import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { type ActionMenuItem } from '../../components/UI/ActionMenu';
import { ResponsiveEntityList } from '../../components/UI/ResponsiveEntityList';
import { Modal } from '../../components/UI/Modal';
import { agentBotService } from '../../services/agentBotService';
import { inboxService } from '../../services/inboxService';
import { workspaceService } from '../../services/workspaceService';
import { AgentBot } from '../../types/agentBot';
import { Inbox } from '../../types/inbox';
import { AGENT_BOT_TYPE_LABELS } from '../../types/atendimentoEnums';
import { useToast } from '../../components/UI/ToastProvider';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

interface WorkspaceOption {
  id: string;
  name: string;
}

export const AgentBotsPage: React.FC = () => {
  const [bots, setBots] = useState<AgentBot[]>([]);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [botToDelete, setBotToDelete] = useState<AgentBot | null>(null);
  const [assignBot, setAssignBot] = useState<AgentBot | null>(null);
  const [assignInboxId, setAssignInboxId] = useState('');
  const { addToast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    try
    {
      const [botsData, inboxesData, workspacesData] = await Promise.all([
        agentBotService.getAll(),
        inboxService.getAll(),
        workspaceService.getWorkspaces(),
      ]);
      setBots(botsData);
      setInboxes(inboxesData);
      setWorkspaces(workspacesData.map(workspace => ({ id: workspace.id, name: workspace.name })));
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao carregar robôs'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const handleAssign = async () => {
    if (!assignBot || !assignInboxId)
    {
      addToast('Selecione uma caixa de entrada', 'error');
      return;
    }

    try
    {
      await agentBotService.assignToInbox(assignBot.id, { inboxId: assignInboxId });
      addToast('Robô vinculado à caixa', 'success');
      setIsAssignModalOpen(false);
      setAssignBot(null);
      setAssignInboxId('');
      await fetchData();
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao vincular robô'), 'error');
    }
  };

  const handleDelete = async () => {
    if (!botToDelete)
    {
      return;
    }

    try
    {
      await agentBotService.delete(botToDelete.id);
      addToast('Robô excluído', 'success');
      setIsDeleteModalOpen(false);
      setBotToDelete(null);
      await fetchData();
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao excluir robô'), 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Robôs de Atendimento</h1>
          <p className="text-gray-600 text-sm mt-1">Bots IA (Copilot)</p>
        </div>
        <Link to={helpdeskRoutes.settingsBotNew}>
          <Button variant="primary">Novo robô</Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : bots.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Nenhum robô configurado.
        </div>
      ) : (
        <ResponsiveEntityList
          items={bots}
          getKey={(bot) => bot.id}
          mobileTitle={(bot) => bot.name}
          mobileTo={(bot) => helpdeskRoutes.settingsBotEdit(bot.id)}
          mobileFields={(bot) => {
            const linkedInboxes = inboxes.filter((inbox) => inbox.agentBotId === bot.id);
            return [
              { label: 'Tipo', value: AGENT_BOT_TYPE_LABELS[bot.botType] },
              {
                label: 'Workspace',
                value: workspaces.find((workspace) => workspace.id === bot.workspaceId)?.name ?? (bot.outgoingUrl ? 'Webhook' : '—'),
              },
              {
                label: 'Caixas',
                value: linkedInboxes.length === 0
                  ? 'Nenhuma'
                  : linkedInboxes.map((inbox) => inbox.name).join(', '),
              },
            ];
          }}
          columns={[
            {
              header: 'Nome',
              cellClassName: 'px-6 py-4 text-sm font-medium text-gray-900',
              cell: (bot) => (
                <Link
                  to={helpdeskRoutes.settingsBotEdit(bot.id)}
                  className="hover:text-blue-700 hover:underline"
                >
                  {bot.name}
                </Link>
              ),
            },
            {
              header: 'Tipo',
              cell: (bot) => AGENT_BOT_TYPE_LABELS[bot.botType],
            },
            {
              header: 'Workspace',
              cell: (bot) => workspaces.find((workspace) => workspace.id === bot.workspaceId)?.name ?? (bot.outgoingUrl ? 'Webhook' : '—'),
            },
            {
              header: 'Caixas',
              cell: (bot) => {
                const linkedInboxes = inboxes.filter((inbox) => inbox.agentBotId === bot.id);
                if (linkedInboxes.length === 0)
                {
                  return 'Nenhuma';
                }

                return (
                  <span className="flex flex-wrap gap-1">
                    {linkedInboxes.map((inbox) => (
                      <span
                        key={inbox.id}
                        className="inline-flex px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                      >
                        {inbox.name}
                      </span>
                    ))}
                  </span>
                );
              },
            },
          ]}
          actions={(bot): ActionMenuItem[] => [
            {
              key: 'edit',
              label: 'Editar',
              to: helpdeskRoutes.settingsBotEdit(bot.id),
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              ),
            },
            {
              key: 'assign',
              label: 'Vincular à caixa',
              onClick: () => {
                setAssignBot(bot);
                setIsAssignModalOpen(true);
              },
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.656-2.828a4 4 0 00-5.656 0l-3 3" />
                </svg>
              ),
            },
            {
              key: 'delete',
              label: 'Excluir',
              variant: 'danger',
              onClick: () => {
                setBotToDelete(bot);
                setIsDeleteModalOpen(true);
              },
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              ),
            },
          ]}
        />
      )}

      <Modal isOpen={isAssignModalOpen} title="Vincular robô à caixa" onClose={() => setIsAssignModalOpen(false)}>
        <p className="text-sm text-gray-600 mb-4">Robô: <strong>{assignBot?.name}</strong></p>
        <select value={assignInboxId} onChange={e => setAssignInboxId(e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm mb-4">
          <option value="">Selecione a caixa de entrada</option>
          {inboxes.map(inbox => (
            <option key={inbox.id} value={inbox.id}>{inbox.name}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsAssignModalOpen(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleAssign}>Vincular</Button>
        </div>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} title="Excluir robô" onClose={() => setIsDeleteModalOpen(false)}>
        <p className="text-gray-600 mb-4">Deseja excluir o robô <strong>{botToDelete?.name}</strong>?</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Excluir</Button>
        </div>
      </Modal>
    </div>
  );
};
